import { Button, Modal } from 'antd'
import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react'

export type TenantVideoPlayerPhase = 'idle' | 'loading' | 'ready' | 'error'

export const TENANT_VIDEO_PLAYER_MAX_WIDTH_PX = 1400
export const TENANT_VIDEO_PLAYER_VIEWPORT_RATIO = 0.9
export const TENANT_VIDEO_PLAYER_MAX_HEIGHT_VH = 82

export function resolveTenantVideoPlayerModalWidth(viewportWidth: number): number {
  return Math.min(Math.round(viewportWidth * TENANT_VIDEO_PLAYER_VIEWPORT_RATIO), TENANT_VIDEO_PLAYER_MAX_WIDTH_PX)
}

export interface TenantVideoPlayerModalProps {
  open: boolean
  onClose: () => void
  title: string
  videoUrl: string | null
  posterUrl?: string | null
  phase: TenantVideoPlayerPhase
  slowHint?: boolean
  errorKind?: 'timeout' | 'media' | null
  reloadNonce?: number
  videoRef?: RefObject<HTMLVideoElement | null>
  onReady?: () => void
  onProgress?: () => void
  onError?: () => void
  onRetry?: () => void
  /** Optional row rendered below the player stage (e.g. source provenance). */
  extraFooter?: ReactNode
}

/**
 * Unified tenant video player modal — Video Generation list, Continuation
 * segments, and composite playback share this surface (P0: size + pause frame).
 *
 * Poster overlay is shown only before the first decoded frame / play event.
 * After playback starts, pause keeps the same `<video>` element and current
 * frame visible (never re-shows poster over a paused frame).
 */
export function TenantVideoPlayerModal({
  open,
  onClose,
  title,
  videoUrl,
  posterUrl,
  phase,
  slowHint = false,
  errorKind,
  reloadNonce = 0,
  videoRef,
  onReady,
  onProgress,
  onError,
  onRetry,
  extraFooter,
}: TenantVideoPlayerModalProps) {
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false)
  const [modalWidth, setModalWidth] = useState(() =>
    typeof window !== 'undefined' ? resolveTenantVideoPlayerModalWidth(window.innerWidth) : TENANT_VIDEO_PLAYER_MAX_WIDTH_PX,
  )
  const internalVideoRef = useRef<HTMLVideoElement | null>(null)
  const resolvedVideoRef = videoRef ?? internalVideoRef

  useEffect(() => {
    if (!open) setHasPlayedOnce(false)
  }, [open])

  useEffect(() => {
    const onResize = () => setModalWidth(resolveTenantVideoPlayerModalWidth(window.innerWidth))
    onResize()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const showPoster = Boolean(posterUrl) && !hasPlayedOnce && phase === 'loading'

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={title}
      width={modalWidth}
      destroyOnClose
      rootClassName="tc-video-gen-player-modal tc-tenant-video-player-modal"
      styles={{ body: { padding: 0 } }}
    >
      {open && phase !== 'error' ? (
        <div className="tc-tenant-video-player-stage">
          {showPoster ? (
            <img src={posterUrl!} alt="" className="tc-tenant-video-player-poster" />
          ) : null}
          {videoUrl ? (
            <video
              ref={resolvedVideoRef}
              key={`${videoUrl}#${reloadNonce}`}
              src={videoUrl}
              controls
              autoPlay
              playsInline
              preload="metadata"
              className="tc-tenant-video-player-video"
              onLoadedMetadata={onReady}
              onCanPlay={onReady}
              onPlaying={() => setHasPlayedOnce(true)}
              onTimeUpdate={(e) => {
                if (e.currentTarget.currentTime > 0) setHasPlayedOnce(true)
              }}
              onProgress={onProgress}
              onError={onError}
            />
          ) : null}
          {phase === 'loading' && (
            <div className="tc-tenant-video-player-loading-hint">
              {slowHint ? '视频加载较慢，正在重试…' : '加载视频…'}
            </div>
          )}
        </div>
      ) : phase === 'error' ? (
        <div className="tc-tenant-video-player-error">
          <div className="tc-tenant-video-player-error-msg">
            {errorKind === 'timeout' ? '视频加载超时，请重试' : '视频加载失败，请重试'}
          </div>
          <Button type="primary" onClick={onRetry}>
            重试
          </Button>
        </div>
      ) : null}
      {extraFooter ? <div className="tc-tenant-video-player-extra-footer">{extraFooter}</div> : null}
    </Modal>
  )
}
